import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Snackbar,
  SvgIcon,
  TextField,
  Typography,
} from "@mui/material";
import { CardMedia } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { signInUser, signInWithGoogle } from "../firebase";
import { isLoggedIn } from "../session";
import logo from "../imgs/logo192.png";

const GoogleIcon = () => (
  <SvgIcon viewBox="0 0 48 48" sx={{ width: 20, height: 20 }}>
    <path fill="#EA4335" d="M24 9.5c3.14 0 5.95 1.08 8.17 2.86l6.1-6.1C34.46 3.14 29.5 1 24 1 14.82 1 7.07 6.48 3.64 14.28l7.1 5.52C12.45 13.56 17.77 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.52 24.5c0-1.64-.15-3.22-.42-4.75H24v9h12.7c-.55 2.97-2.2 5.49-4.68 7.19l7.19 5.59C43.49 37.28 46.52 31.36 46.52 24.5z"/>
    <path fill="#FBBC05" d="M10.74 28.2A14.6 14.6 0 0 1 9.5 24c0-1.46.25-2.87.74-4.2l-7.1-5.52A23.94 23.94 0 0 0 0 24c0 3.87.93 7.53 2.56 10.77l8.18-6.57z"/>
    <path fill="#34A853" d="M24 47c5.5 0 10.12-1.82 13.5-4.94l-7.19-5.59C28.5 37.85 26.36 38.5 24 38.5c-6.23 0-11.55-4.06-13.26-9.79l-8.18 6.57C6.07 42.52 14.46 47 24 47z"/>
    <path fill="none" d="M0 0h48v48H0z"/>
  </SvgIcon>
);

const FIREBASE_ERROR_MESSAGES = {
  "auth/user-not-found": "No existe una cuenta con ese correo electrónico.",
  "auth/wrong-password": "Contraseña incorrecta. Inténtalo de nuevo.",
  "auth/invalid-email": "El formato del correo electrónico no es válido.",
  "auth/user-disabled": "Esta cuenta ha sido deshabilitada.",
  "auth/too-many-requests": "Demasiados intentos fallidos. Espera un momento antes de volver a intentarlo.",
  "auth/network-request-failed": "Error de red. Verifica tu conexión a internet.",
  "auth/invalid-credential": "Correo o contraseña incorrectos. Inténtalo de nuevo.",
};

const getErrorMessage = (error) => {
  const code = error?.code || "";
  return FIREBASE_ERROR_MESSAGES[code] || "Ocurrió un error inesperado. Inténtalo de nuevo.";
};

export default function Login() {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [snackbar] = useState({ open: false, message: "" });

  const onSubmit = async (event) => {
    event.preventDefault();

    if (!email || !password) {
      setError("Por favor ingrese su correo y contraseña.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      let loginResponse = await signInUser(email, password);
      isLoggedIn(loginResponse.user);
      navigate("/home");
    } catch (err) {
      console.error(err.message);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError("");
    setGoogleLoading(true);
    try {
      const result = await signInWithGoogle();
      isLoggedIn(result.user);
      navigate("/home");
    } catch (err) {
      if (err.code !== "auth/popup-closed-by-user") {
        setError(getErrorMessage(err));
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const inputSx = {
    mt: 2,
    "& .MuiInputBase-root": { color: "white" },
    "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.7)" },
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: "rgba(255,255,255,0.35)" },
      "&:hover fieldset": { borderColor: "rgba(255,255,255,0.7)" },
      "&.Mui-focused fieldset": { borderColor: "white" },
    },
    "& .MuiInputLabel-root.Mui-focused": { color: "white" },
    "& .MuiIconButton-root": { color: "rgba(255,255,255,0.7)" },
  };

  return (
    <Box
      sx={{
        minWidth: "100vw",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Container maxWidth="xs">
        <Box
          sx={{
            background: "rgba(255,255,255,0.07)",
            backdropFilter: "blur(12px)",
            borderRadius: 3,
            border: "1px solid rgba(255,255,255,0.12)",
            p: { xs: 3, sm: 4 },
            boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
          }}
        >
          <CardMedia
            component="img"
            image={logo}
            title="Chatio"
            sx={{
              width: 72,
              height: 72,
              mx: "auto",
              mb: 2,
              borderRadius: "50%",
              boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
            }}
          />
          <Typography
            variant="h5"
            component="h1"
            textAlign="center"
            sx={{ fontWeight: 600, color: "white", mb: 0.5 }}
          >
            Bienvenido de nuevo
          </Typography>
          <Typography
            variant="body2"
            textAlign="center"
            sx={{ color: "rgba(255,255,255,0.55)", mb: 2 }}
          >
            Inicia sesión en tu cuenta
          </Typography>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                background: "rgba(211,47,47,0.15)",
                color: "#ff8a80",
                border: "1px solid rgba(211,47,47,0.3)",
                "& .MuiAlert-icon": { color: "#ff8a80" },
              }}
            >
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={onSubmit} noValidate>
            <TextField
              label="Correo electrónico"
              variant="outlined"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={inputSx}
              fullWidth
              disabled={loading}
            />
            <TextField
              label="Contraseña"
              variant="outlined"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={inputSx}
              fullWidth
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                      tabIndex={-1}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box textAlign="right" sx={{ mt: 0.5 }}>
              <Link
                href="/forgot-password"
                underline="hover"
                sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.6)" }}
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </Box>

            <Button
              variant="contained"
              type="submit"
              disabled={loading}
              fullWidth
              sx={{
                mt: 2.5,
                py: 1.3,
                fontWeight: 600,
                fontSize: "0.95rem",
                background: "linear-gradient(90deg, #e040fb, #7c4dff)",
                borderRadius: 2,
                boxShadow: "0 4px 16px rgba(124,77,255,0.35)",
                "&:hover": {
                  background: "linear-gradient(90deg, #ce39e0, #651fff)",
                  boxShadow: "0 4px 20px rgba(124,77,255,0.55)",
                },
                "&.Mui-disabled": { opacity: 0.6 },
              }}
            >
              {loading ? (
                <CircularProgress size={22} sx={{ color: "white" }} />
              ) : (
                "Iniciar sesión"
              )}
            </Button>
          </Box>

          <Divider sx={{ my: 3, borderColor: "rgba(255,255,255,0.12)" }}>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)" }}>
              O continúa con
            </Typography>
          </Divider>

          <Button
            variant="outlined"
            fullWidth
            disabled={loading || googleLoading}
            onClick={handleGoogleSignIn}
            startIcon={
              googleLoading ? (
                <CircularProgress size={18} sx={{ color: "white" }} />
              ) : (
                <GoogleIcon />
              )
            }
            sx={{
              py: 1.2,
              borderColor: "rgba(255,255,255,0.3)",
              color: "white",
              fontWeight: 500,
              borderRadius: 2,
              "&:hover": {
                borderColor: "white",
                background: "rgba(255,255,255,0.06)",
              },
              "&.Mui-disabled": { opacity: 0.5 },
            }}
          >
            Continuar con Google
          </Button>

          <Divider sx={{ my: 3, borderColor: "rgba(255,255,255,0.12)" }} />

          <Typography textAlign="center" sx={{ color: "rgba(255,255,255,0.55)", fontSize: "0.9rem" }}>
            ¿No tienes cuenta?{" "}
            <Link
              href="/register"
              underline="hover"
              sx={{ color: "white", fontWeight: 600 }}
            >
              Regístrate
            </Link>
          </Typography>
        </Box>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        message={snackbar.message}
      />
    </Box>
  );
}
